import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, clearToken, clearRefreshToken, getToken, setToken, setRefreshToken, storage, WORKSPACE_KEY } from "./api";

const Ctx = createContext(null);
const DEVICE_NAME = "web app";
const DEVICE_PLATFORM = "web";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspace, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshWorkspaces = useCallback(async () => {
    const ws = await api("/workspaces");
    setWorkspaces(ws);
    const savedId = storage.get(WORKSPACE_KEY, "");
    const current = ws.find((w) => w.id === savedId) || ws[0] || null;
    setActive(current);
    if (current) storage.set(WORKSPACE_KEY, current.id);
    return ws;
  }, []);

  const refreshMe = useCallback(async () => {
    const me = await api("/auth/me");
    setUser(me);
    return me;
  }, []);

  const reset = () => { clearToken(); clearRefreshToken(); setUser(null); setWorkspaces([]); setActive(null); };

  const bootstrap = useCallback(async () => {
    setLoading(true);
    try {
      if (!getToken()) { setUser(null); setWorkspaces([]); setActive(null); return; }
      const me = await api("/auth/me");
      setUser(me);
      await refreshWorkspaces();
    } catch {
      reset();
    } finally {
      setLoading(false);
    }
  }, [refreshWorkspaces]);

  useEffect(() => {
    (async () => {
      // Complete the Google redirect flow (session_id in URL hash/query) before bootstrapping.
      const search = window.location.search + window.location.hash;
      const m = search.match(/[?#&]session_id=([^&]+)/);
      if (m) {
        try {
          const res = await api("/auth/google", { method: "POST", body: { session_id: decodeURIComponent(m[1]) }, auth: false });
          setToken(res.access_token);
          setRefreshToken(res.refresh_token);
        } catch { /* fall through to normal bootstrap */ }
        window.history.replaceState(null, "", window.location.pathname);
      }
      await bootstrap();
    })();
  }, [bootstrap]);

  const signIn = useCallback(async ({ identifier, password }) => {
    const res = await api("/auth/login", {
      method: "POST", auth: false,
      body: { identifier, password, device_name: DEVICE_NAME, device_platform: DEVICE_PLATFORM },
    });
    setToken(res.access_token);
    setRefreshToken(res.refresh_token);
    setUser(res.user);
    await refreshWorkspaces();
  }, [refreshWorkspaces]);

  const signUp = useCallback(async (input) => {
    const res = await api("/auth/register", { method: "POST", body: input, auth: false });
    setToken(res.access_token);
    setRefreshToken(res.refresh_token);
    setUser(res.user);
    await refreshWorkspaces();
  }, [refreshWorkspaces]);

  const signOut = useCallback(async () => {
    try { await api("/auth/logout", { method: "POST" }); } catch { /* noop */ }
    storage.remove(WORKSPACE_KEY);
    reset();
  }, []);

  const setActiveWorkspace = useCallback(async (id) => {
    const ws = workspaces.find((w) => w.id === id) || null;
    setActive(ws);
    if (ws) storage.set(WORKSPACE_KEY, ws.id);
  }, [workspaces]);

  const createWorkspace = useCallback(async (name, type) => {
    const ws = await api("/workspaces", { method: "POST", body: { name, type } });
    setWorkspaces((prev) => [...prev, ws]);
    setActive(ws);
    storage.set(WORKSPACE_KEY, ws.id);
    return ws;
  }, []);

  const value = useMemo(() => ({
    user, workspaces, activeWorkspace, loading, bootstrap, signIn, signUp, signOut,
    refreshMe, refreshWorkspaces, setActiveWorkspace, createWorkspace,
  }), [user, workspaces, activeWorkspace, loading, bootstrap, signIn, signUp, signOut, refreshMe, refreshWorkspaces, setActiveWorkspace, createWorkspace]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used within AuthProvider");
  return v;
}

export const isManagerRole = (role) => ["owner", "admin", "manager"].includes(role || "");
